// Post-Signup Video Verification Component
// Used after account creation for identity/liveness verification
// Simplified approach: No paper documents required - just face + spoken declaration
import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  Video, Camera, StopCircle, Upload, CheckCircle, XCircle, 
  AlertTriangle, Loader2, RefreshCw, User, Shield,
  Clock, Eye, Play, Scan
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PostSignupVideoVerification({ 
  user,
  companyName,
  fullName,
  onVideoUploaded,
  onClose
}) {
  const [step, setStep] = useState('instructions'); // instructions, recording, preview, uploading, complete
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cameraError, setCameraError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [streamActive, setStreamActive] = useState(false);
  
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const MAX_RECORDING_TIME = 30; // seconds
  const MIN_RECORDING_TIME = 15; // seconds

  // Generate the declaration text dynamically
  const declarationText = `I am ${fullName || '[Your Name]'} from ${companyName || '[Your Company]'}, applying for GlobalVyapar`;

  // Effect to attach stream to video element when step is 'recording' and video element is mounted
  useEffect(() => {
    if (step === 'recording' && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.playsInline = true;
      videoRef.current.play().catch(e => console.log('Autoplay handled:', e));
    }
  }, [step]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      
      // Request camera with specific constraints for Safari compatibility
      const constraints = {
        video: { 
          facingMode: 'user',
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 }
        },
        audio: true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setStreamActive(true);
      setStep('recording'); // This triggers re-render, useEffect will attach stream
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera and microphone access in your browser settings to continue.');
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found. Please ensure your device has a camera connected.');
      } else {
        setCameraError(`Could not access camera: ${error.message}. Please check your device settings.`);
      }
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      toast.error('Camera not ready. Please try again.');
      return;
    }
    
    chunksRef.current = [];
    
    // Try different MIME types for browser compatibility
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];
    
    let selectedMimeType = '';
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        selectedMimeType = mimeType;
        break;
      }
    }
    
    try {
      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
    } catch (e) {
      console.log('MediaRecorder fallback:', e);
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
    }
    
    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const mimeType = mediaRecorderRef.current.mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setRecordedBlob(blob);
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);
      setStep('preview');
      
      // Stop camera stream after recording
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        setStreamActive(false);
      }
    };
    
    mediaRecorderRef.current.start(1000); // Collect data every second
    setIsRecording(true);
    setRecordingTime(0);
    
    // Timer with auto-stop
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= MAX_RECORDING_TIME - 1) {
          stopRecording();
          return MAX_RECORDING_TIME;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const retakeVideo = async () => {
    // Clean up previous recording
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setStep('instructions');
  };

  const uploadVideo = async () => {
    if (!recordedBlob) {
      toast.error('No video recorded');
      return;
    }

    setStep('uploading');
    setUploadProgress(0);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64data = reader.result.split(',')[1];
        const token = localStorage.getItem('token');
        
        // Simulate progress while uploading
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 500);

        try {
          const response = await axios.post(
            `${API}/api/seller-verification/video/upload`,
            {
              video_data: base64data,
              expected_data: {
                full_name: fullName,
                company_name: companyName,
                verification_type: 'liveness'
              }
            },
            {
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          clearInterval(progressInterval);
          setUploadProgress(100);
          setUploadResult(response.data);
          setStep('complete');
          
          if (response.data.ai_recommendation === 'APPROVE') {
            toast.success('Video verification submitted! AI detected your face clearly.');
          } else if (response.data.ai_recommendation === 'MANUAL_REVIEW') {
            toast.info('Video submitted for admin review. Usually takes 1-2 business days.');
          } else {
            toast.warning('Please ensure your face is clearly visible and try again.');
          }

          if (onVideoUploaded) {
            onVideoUploaded(response.data);
          }
        } catch (error) {
          clearInterval(progressInterval);
          console.error('Upload error:', error);
          toast.error(error.response?.data?.detail || 'Failed to upload video. Please try again.');
          setStep('preview');
        }
      };
      
      reader.onerror = () => {
        toast.error('Failed to process video');
        setStep('preview');
      };
      
      reader.readAsDataURL(recordedBlob);
    } catch (error) {
      console.error('Error processing video:', error);
      toast.error('Failed to process video');
      setStep('preview');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [recordedUrl]);

  // Instructions Step
  if (step === 'instructions') {
    return (
      <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="video-verification-card">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Scan className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Identity Verification</CardTitle>
              <CardDescription>Quick 15-30 second video to verify your identity</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* What You'll Do */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2 text-slate-800 font-medium mb-3">
              <Video className="h-4 w-4" />
              Simple 3-Step Process
            </div>
            <ol className="space-y-3 text-sm text-slate-600">
              <li className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0 font-medium">
                  1
                </span>
                <div>
                  <p className="font-medium text-slate-800">Face the camera</p>
                  <p className="text-slate-500">Position yourself so your face is clearly visible</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0 font-medium">
                  2
                </span>
                <div>
                  <p className="font-medium text-slate-800">Read the declaration</p>
                  <p className="text-slate-500">Say: &quot;{declarationText}&quot;</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0 font-medium">
                  3
                </span>
                <div>
                  <p className="font-medium text-slate-800">Follow liveness prompt</p>
                  <p className="text-slate-500">Blink naturally or turn your head slightly when prompted</p>
                </div>
              </li>
            </ol>
          </div>

          {/* Liveness Tips */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
              <User className="h-4 w-4" />
              Tips for Success
            </div>
            <ul className="text-sm text-green-700 space-y-1">
              <li>• Use good lighting - face a window or light source</li>
              <li>• Keep your face centered and clearly visible</li>
              <li>• Speak clearly when reading the declaration</li>
              <li>• No masks, sunglasses, or face coverings</li>
            </ul>
          </div>

          {/* Declaration Preview */}
          <div className="bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">You will say:</p>
            <p className="text-sm font-medium text-slate-800 italic">&quot;{declarationText}&quot;</p>
          </div>

          {/* Info Summary */}
          <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4">
            <div>
              <p className="text-xs text-slate-500">Duration</p>
              <p className="font-medium text-slate-800">15-30 seconds</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">What AI checks</p>
              <p className="font-medium text-slate-800">Face detection & liveness</p>
            </div>
          </div>

          {/* Camera Error */}
          {cameraError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{cameraError}</AlertDescription>
            </Alert>
          )}

          {/* Start Button */}
          <Button 
            onClick={startCamera} 
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="start-camera-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            Start Camera
          </Button>
          
          {onClose && (
            <Button 
              onClick={onClose} 
              variant="ghost"
              className="w-full text-slate-500"
              data-testid="skip-video-btn"
            >
              I&apos;ll do this later
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Recording Step
  if (step === 'recording') {
    return (
      <Card className="border-2 border-blue-200" data-testid="video-recording-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {isRecording ? (
              <>
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                Recording...
              </>
            ) : (
              <>
                <Camera className="h-5 w-5" />
                Camera Ready
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Video Preview - Fixed for Safari */}
          <div 
            className="relative bg-black rounded-lg overflow-hidden"
            style={{ aspectRatio: '16/9', minHeight: '300px', maxHeight: '400px' }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                transform: 'scaleX(-1)', // Mirror for selfie view
                display: 'block',
                backgroundColor: '#000'
              }}
              data-testid="live-video-feed"
            />
            
            {/* Recording Timer Overlay */}
            {isRecording && (
              <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 z-10">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                {recordingTime}s / {MAX_RECORDING_TIME}s
              </div>
            )}

            {/* Instructions Overlay - Only when not recording */}
            {!isRecording && streamActive && (
              <div className="absolute inset-0 flex items-end justify-center pb-6 bg-gradient-to-t from-black/70 to-transparent z-10">
                <div className="text-center text-white px-4">
                  <p className="text-lg font-medium mb-2">Position yourself and press Record</p>
                  <p className="text-sm opacity-90">Make sure your face is clearly visible</p>
                </div>
              </div>
            )}
            
            {/* Declaration Prompt - Show while recording */}
            {isRecording && (
              <div className="absolute bottom-4 left-4 right-4 bg-blue-600/90 text-white px-4 py-3 rounded-lg z-10">
                <p className="text-xs mb-1 opacity-80">Please say:</p>
                <p className="text-sm font-medium">&quot;{declarationText}&quot;</p>
                {recordingTime >= 10 && recordingTime <= 15 && (
                  <p className="text-xs mt-2 text-yellow-200">💡 Now blink naturally or turn your head slightly</p>
                )}
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {isRecording && (
            <div className="space-y-1">
              <Progress value={(recordingTime / MAX_RECORDING_TIME) * 100} className="h-2" />
              <div className="flex justify-between text-xs text-slate-500">
                <span>Min: {MIN_RECORDING_TIME}s</span>
                <span>Max: {MAX_RECORDING_TIME}s</span>
              </div>
            </div>
          )}

          {/* Recording Controls */}
          <div className="flex gap-3">
            {!isRecording ? (
              <Button 
                onClick={startRecording} 
                className="flex-1 bg-red-600 hover:bg-red-700"
                data-testid="start-recording-btn"
              >
                <Video className="h-4 w-4 mr-2" />
                Start Recording
              </Button>
            ) : (
              <Button 
                onClick={stopRecording} 
                variant="outline"
                className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
                disabled={recordingTime < MIN_RECORDING_TIME}
                data-testid="stop-recording-btn"
              >
                <StopCircle className="h-4 w-4 mr-2" />
                {recordingTime < MIN_RECORDING_TIME 
                  ? `Wait ${MIN_RECORDING_TIME - recordingTime}s...` 
                  : `Stop Recording (${MAX_RECORDING_TIME - recordingTime}s left)`
                }
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Preview Step
  if (step === 'preview') {
    return (
      <Card className="border-2 border-green-200" data-testid="video-preview-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5 text-green-600" />
            Review Your Video
          </CardTitle>
          <CardDescription>Make sure your face is clearly visible and you can hear your declaration</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Video Preview - Full width responsive */}
          <div 
            className="relative bg-black rounded-lg overflow-hidden"
            style={{ aspectRatio: '16/9', minHeight: '300px', maxHeight: '450px' }}
          >
            <video
              ref={previewRef}
              src={recordedUrl}
              controls
              playsInline
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                display: 'block',
                backgroundColor: '#000'
              }}
              data-testid="preview-video"
            />
          </div>

          {/* Checklist */}
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="font-medium text-slate-800 mb-2">Before submitting, verify:</p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Your face is clearly visible throughout
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                You can hear your declaration clearly
              </li>
              <li className="flex items-center gap-2 text-slate-600">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Good lighting with no shadows on face
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={retakeVideo} 
              variant="outline"
              className="flex-1"
              data-testid="retake-video-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retake
            </Button>
            <Button 
              onClick={uploadVideo} 
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="submit-video-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Submit for Review
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Uploading Step
  if (step === 'uploading') {
    return (
      <Card className="border-2 border-blue-200" data-testid="video-uploading-card">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
              <Scan className="h-5 w-5 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="text-center">
              <p className="font-medium text-slate-800">Uploading & Analyzing</p>
              <p className="text-sm text-slate-500 mt-1">AI is checking for face detection and liveness...</p>
            </div>
            <div className="w-full max-w-xs">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-center text-xs text-slate-500 mt-2">{uploadProgress}% complete</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Complete Step
  if (step === 'complete') {
    const isApproved = uploadResult?.ai_recommendation === 'APPROVE';
    const isPending = uploadResult?.ai_recommendation === 'MANUAL_REVIEW';
    
    return (
      <Card className={`border-2 ${isApproved ? 'border-green-200 bg-green-50/30' : 'border-yellow-200 bg-yellow-50/30'}`} data-testid="video-complete-card">
        <CardContent className="pt-6 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isApproved ? 'bg-green-100' : 'bg-yellow-100'}`}>
            {isApproved ? (
              <CheckCircle className="h-8 w-8 text-green-600" />
            ) : (
              <Clock className="h-8 w-8 text-yellow-600" />
            )}
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isApproved ? 'text-green-800' : 'text-yellow-800'}`}>
            {isApproved ? 'Verification Submitted!' : 'Video Under Review'}
          </h3>
          <p className={`text-sm ${isApproved ? 'text-green-700' : 'text-yellow-700'}`}>
            {isApproved 
              ? `AI detected your face clearly (${uploadResult?.ai_match_percentage || 0}% confidence). An admin will complete the final review.`
              : 'Your video has been submitted. Our team will review it within 1-2 business days.'
            }
          </p>
          
          {onClose && (
            <Button 
              onClick={onClose}
              className="mt-4"
              variant={isApproved ? 'default' : 'outline'}
            >
              Continue to Dashboard
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
}
